import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

import { catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';

import {
  DecimalOnlyDirective,
  DigitsOnlyDirective,
  PhoneOnlyDirective,
} from '../../core/directives';

import { EquipmentService } from '../../core/services/equipment.service';
import { InventoryService } from '../../core/services/inventory.service';
import { SettingsService } from '../../core/services/settings.service';
import { ExportService } from '../../core/services/export.service';
import { AuthService } from '../auth/auth.service';
import {
  InventoryItem,
  StockIssue,
  StockLocationSuggestion,
} from '../../core/models/inventory.models';
import { isBranchFilterLocked } from '../../core/utils/branch-scope.util';
import { matchesItemQuery } from '../../core/utils/item-filter.util';
import {
  currentTimestamp,
  formatCurrency,
  formatDate,
  stockLevelClass,
} from '../../core/utils/format.util';
import { environment } from '../../environments/environment';
import {
  assertCanTake,
  parseNonNegativeNumber,
  parsePositiveInt,
} from '../../core/utils/stock-qty.util';

interface IssueLineItem {
  itemId: string;
  code: string;
  name: string;
  category: string;
  available: number;
  quantity: number;
  reserved: boolean;
  price: number;
  serialNumber?: string;
  equipmentAssetId?: string;
  unavailableReason?: string;
  icon?: string;
}

interface IssueSerialOption {
  id: string;
  serialNumber: string;
  location: string;
}

interface PendingIssuePick {
  item: PickerItemView;
  serialNumber: string;
  equipmentAssetId: string;
  serialOptions: IssueSerialOption[];
  serialsLoading: boolean;
}

interface PickerItemView {
  id: string;
  code: string;
  name: string;
  category: string;
  icon?: string;
  available: number;
  unavailableReason: string;
  canIssue: boolean;
  suggestions: StockLocationSuggestion[];
  suggestionText: string;
  price: number;
}

@Component({
  selector: 'app-issue-stock',
  imports: [
    FormsModule,
    CommonModule,
    RouterLink,
    DigitsOnlyDirective,
    DecimalOnlyDirective,
    PhoneOnlyDirective,
  ],
  standalone: true,
  templateUrl: './issue-stock.component.html',
  styleUrl: './issue-stock.component.css',
})
export class IssueStockComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly equipment = inject(EquipmentService);
  private readonly settings = inject(SettingsService);
  private readonly auth = inject(AuthService);
  private readonly exporter = inject(ExportService);

  readonly timestamp = currentTimestamp;
  readonly formatCurrency = formatCurrency;
  readonly formatDate = formatDate;
  readonly stockLevelClass = stockLevelClass;
  readonly issuedOutStock = this.inventory.issuedOutStock;
  readonly issueStockList = this.inventory.issueStockList;
  readonly recentItemFilter = signal('');
  readonly recentIssues = computed(() => {
    const query = this.recentItemFilter();
    return this.issueStockList()
      .filter((issue) =>
        matchesItemQuery(query, issue.itemName, issue.issueNumber),
      )
      .slice(0, 12);
  });
  readonly reservedIssueHolds = this.inventory.reservedIssueHolds;
  readonly branches = this.settings.scopedBranches;
  readonly branchSelectionLocked = computed(() =>
    isBranchFilterLocked(this.auth),
  );

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (this.branchId() || !branches.length) {
        return;
      }
      if (this.auth.currentBranchId() > 0) {
        this.branchId.set(String(this.auth.currentBranchId()));
      } else if (branches.length === 1) {
        this.branchId.set(branches[0].id);
      }
    });
  }

  ngOnInit(): void {
    this.issuedBy.set(this.auth.currentUserName());
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.refreshFromApi();
    }
  }

  readonly issuedToCase = signal('');
  readonly recipientPhone = signal('');
  readonly recipientLocation = signal('');
  readonly issueDate = signal(new Date().toISOString().slice(0, 10));
  readonly issuedBy = signal('');
  readonly branchId = signal('');
  readonly locationId = signal('');
  readonly notes = signal('');

  readonly itemSearch = signal('');
  readonly pendingPicks = signal<PendingIssuePick[]>([]);
  readonly lineItems = signal<IssueLineItem[]>([]);

  readonly showItemModal = signal(false);
  readonly showConfirmModal = signal(false);
  readonly showEditIssueModal = signal(false);
  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');
  readonly lastIssuedTo = signal('');
  readonly editingIssueId = signal<string | null>(null);
  readonly editingIssueStatus = signal<StockIssue['status'] | null>(null);
  readonly submitting = signal(false);
  readonly saving = signal(false);

  readonly locationsForBranch = computed(() =>
    this.settings.getLocations(this.branchId()),
  );

  readonly isEditingIssue = computed(() => this.editingIssueId() !== null);

  readonly editIssue = signal({
    issuedTo: '',
    branch: '',
    issueDate: '',
    items: 1,
    status: 'pending' as StockIssue['status'],
  });

  readonly filteredPickerItems = computed(() => {
    const search = this.itemSearch().trim().toLowerCase();
    const quantityOnly = new Set(
      this.lineItems()
        .filter((row) => !row.serialNumber)
        .map((row) => row.itemId),
    );
    const branchId = this.branchId();
    const locationId = this.locationId();

    const catalog = this.inventory
      .allItems()
      // Issue Stock is consumables only. Rentables go out through Issue Items
      // so they come back via Check-In.
      .filter((item) => item.status === 'active' && !item.isRentable);
    const known = new Set(catalog.map((item) => item.id));
    const fromStock: InventoryItem[] = [];
    for (const snap of this.inventory.allStockSnapshots()) {
      const id = String(snap.inventoryItemId ?? '').trim();
      if (!id || known.has(id)) {
        continue;
      }
      if (
        branchId &&
        snap.branchId &&
        snap.branchId !== branchId &&
        Number(snap.branchId) !== Number(branchId)
      ) {
        continue;
      }
      if ((snap.quantity ?? 0) <= 0) {
        continue;
      }
      const linked = this.inventory.allItems().find((item) => item.id === id);
      if (linked?.isRentable || (linked && linked.status !== 'active')) {
        continue;
      }
      known.add(id);
      fromStock.push({
        id,
        code: snap.itemCode,
        name: snap.itemName,
        category: snap.category,
        unit: snap.unit,
        stockLevel: snap.quantity,
        reorderLevel: 0,
        price: snap.unitValue,
        status: 'active',
        branchId: snap.branchId,
        locationId: snap.locationId,
        isRentable: false,
      });
    }

    return (
      [...catalog, ...fromStock]
        .map((item) => this.toPickerItem(item, branchId, locationId))
        .filter((item) => {
          if (quantityOnly.has(item.id) || !item.canIssue) {
            return false;
          }
          return matchesItemQuery(
            search,
            item.code,
            item.name,
            item.category,
            item.unavailableReason,
            item.suggestionText,
          );
        })
    );
  });

  private toPickerItem(
    item: InventoryItem,
    branchId: string,
    locationId: string,
  ): PickerItemView {
    const locationName = this.locationsForBranch().find(
      (row) => row.id === locationId,
    )?.name;
    const suggestions = this.inventory.suggestStockLocations(item.id, {
      branchId,
      locationId,
    });
    const suggestionText = this.inventory.formatStockSuggestions(suggestions);

    let unavailableReason = '';
    let available = 0;

    if (item.status === 'inactive' || item.status === 'discontinued') {
      unavailableReason = `Item is ${item.status}`;
    } else if (!branchId || !locationId) {
      unavailableReason = 'Select a branch and store location first';
    } else {
      available = Math.max(
        0,
        this.inventory.availableQuantityAtLocation(
          item.id,
          branchId,
          locationId,
          locationName,
        ) -
          this.equipment.unavailableSerialCount(
            Number(item.id) || 0,
            branchId,
            locationId,
            locationName,
          ),
      );
      if (available <= 0) {
        unavailableReason = suggestionText
          ? `Not available here. Available at ${suggestionText}`
          : 'No available quantity at the selected store location';
      }
    }

    return {
      id: item.id,
      code: item.code,
      name: item.name,
      category: item.category,
      icon: item.icon,
      available,
      unavailableReason,
      canIssue: !unavailableReason && available > 0,
      suggestions,
      suggestionText,
      price: item.price,
    };
  }

  readonly totalItems = computed(() => this.lineItems().length);

  readonly totalQuantity = computed(() =>
    this.lineItems().reduce((sum, row) => sum + row.quantity, 0),
  );

  readonly reservedCount = computed(
    () => this.lineItems().filter((row) => row.reserved).length,
  );

  readonly consumableIssuedCount = computed(
    () => this.lineItems().filter((row) => !row.reserved).length,
  );

  readonly estimatedValue = computed(() =>
    this.lineItems().reduce((sum, row) => sum + row.quantity * row.price, 0),
  );

  readonly selectedBranchName = computed(
    () => this.branches().find((row) => row.id === this.branchId())?.name ?? '',
  );

  readonly selectedLocationName = computed(
    () =>
      this.locationsForBranch().find((row) => row.id === this.locationId())
        ?.name ?? '',
  );

  readonly isFormValid = computed(
    () =>
      this.issuedToCase().trim().length > 0 &&
      this.recipientPhone().trim().length > 0 &&
      this.recipientLocation().trim().length > 0 &&
      this.branchId().trim().length > 0 &&
      this.locationId().trim().length > 0 &&
      this.lineItems().length > 0 &&
      this.issueDate().length > 0,
  );

  onBranchChange(branchId: string): void {
    this.branchId.set(branchId);
    this.locationId.set('');
    this.lineItems.set([]);
  }

  onLocationChange(locationId: string): void {
    this.locationId.set(locationId);
    this.lineItems.set([]);
  }

  openItemModal(): void {
    if (!this.branchId() || !this.locationId()) {
      this.showToastMessage(
        'Select a branch and store location before adding items',
        'error',
      );
      return;
    }
    this.itemSearch.set('');
    this.pendingPicks.set([]);
    this.showItemModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeItemModal(): void {
    this.showItemModal.set(false);
    document.body.style.overflow = 'auto';
  }

  isPicked(itemId: string): boolean {
    return this.pendingPicks().some((pick) => pick.item.id === itemId);
  }

  canAddPending(): boolean {
    const picks = this.pendingPicks();
    if (picks.length === 0) {
      return false;
    }
    return picks.every(
      (pick) =>
        !pick.serialsLoading &&
        (pick.serialOptions.length === 0 || !!pick.serialNumber.trim()),
    );
  }

  toggleItem(item: PickerItemView): void {
    if (!item.canIssue) {
      this.showToastMessage(
        item.unavailableReason ||
          'This item is not available to issue from the selected location',
        'error',
      );
      return;
    }
    if (this.isPicked(item.id)) {
      this.pendingPicks.update((list) =>
        list.filter((pick) => pick.item.id !== item.id),
      );
      return;
    }
    this.pendingPicks.update((list) => [
      ...list,
      {
        item,
        serialNumber: '',
        equipmentAssetId: '',
        serialOptions: [],
        serialsLoading: true,
      },
    ]);
    this.loadSerials(item.id);
  }

  setPendingSerial(itemId: string, serialNumber: string): void {
    const serial = serialNumber.trim();
    this.pendingPicks.update((list) =>
      list.map((pick) => {
        if (pick.item.id !== itemId) {
          return pick;
        }
        const chosen = pick.serialOptions.find((row) => row.serialNumber === serial);
        return {
          ...pick,
          serialNumber: serial,
          equipmentAssetId: chosen?.id ?? '',
        };
      }),
    );
  }

  private isRealSerial(serial: string): boolean {
    const value = serial.trim();
    return !!value && !/^AST-\d+/i.test(value);
  }

  private loadSerials(itemId: string): void {
    const selected = this.inventory.allItems().find((row) => row.id === itemId);
    const name = (selected?.name ?? '').trim().toLowerCase();
    const catalogIds = [
      ...new Set(
        this.inventory
          .allItems()
          .filter((item) => !!name && item.name.trim().toLowerCase() === name)
          .map((item) => Number(item.id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    ];
    const selectedId = Number(itemId);
    if (Number.isFinite(selectedId) && selectedId > 0 && !catalogIds.includes(selectedId)) {
      catalogIds.push(selectedId);
    }

    forkJoin(
      catalogIds.map((id) =>
        this.inventory.fetchItemHistory(id).pipe(
          map((result) => result.data?.assets ?? []),
          catchError(() => of([])),
        ),
      ),
    ).subscribe({
      next: (groups) => {
        if (!this.isPicked(itemId)) {
          return;
        }
        const used = new Set(
          this.lineItems()
            .filter((row) => row.serialNumber)
            .map((row) => (row.serialNumber ?? '').trim().toLowerCase()),
        );
        const blocked = new Set([
          'ISSUED',
          'CHECKED_OUT',
          'CHECKED OUT',
          'IN_REPAIR',
          'IN REPAIR',
          'RESERVED',
          'DAMAGED',
          'RETIRED',
          'DISPOSED',
        ]);
        const options = groups.flat().flatMap((asset) => {
          const serial = (asset.serialNumber || asset.assetNumber || '').trim();
          if (!this.isRealSerial(serial) || used.has(serial.toLowerCase())) {
            return [];
          }
          const status = (asset.status || 'AVAILABLE').trim().toUpperCase();
          if (blocked.has(status)) {
            return [];
          }
          return [
            {
              id: asset.id,
              serialNumber: serial,
              location: asset.locationName || '',
            } satisfies IssueSerialOption,
          ];
        });
        const seen = new Set<string>();
        const serialOptions = options.filter((option) => {
          const key = option.serialNumber.toLowerCase();
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });
        this.pendingPicks.update((list) =>
          list.map((pick) =>
            pick.item.id === itemId
              ? { ...pick, serialOptions, serialsLoading: false }
              : pick,
          ),
        );
      },
      error: () => {
        this.pendingPicks.update((list) =>
          list.map((pick) =>
            pick.item.id === itemId ? { ...pick, serialsLoading: false } : pick,
          ),
        );
      },
    });
  }

  addSelectedItem(): void {
    const picks = this.pendingPicks();
    if (picks.length === 0) {
      return;
    }
    if (picks.some((pick) => pick.serialsLoading)) {
      this.showToastMessage('Serial numbers are still loading', 'info');
      return;
    }

    const missingSerial = picks.find(
      (pick) => pick.serialOptions.length > 0 && !pick.serialNumber.trim(),
    );
    if (missingSerial) {
      this.showToastMessage(
        `Select a serial number for ${missingSerial.item.name}`,
        'error',
      );
      return;
    }

    const lines = [...this.lineItems()];
    for (const pick of picks) {
      const serial = pick.serialNumber.trim();
      const taken = lines
        .filter((row) => row.itemId === pick.item.id)
        .reduce((sum, row) => sum + row.quantity, 0);
      const available = this.liveAvailableFor(pick.item.id);
      if (taken + 1 > available) {
        this.showToastMessage(
          `Cannot add more ${pick.item.name} than the ${available} available.`,
          'error',
        );
        return;
      }

      if (
        serial &&
        lines.some(
          (row) => (row.serialNumber ?? '').toLowerCase() === serial.toLowerCase(),
        )
      ) {
        this.showToastMessage(
          `Serial number ${serial} is already on this issue.`,
          'error',
        );
        return;
      }

      const existingIndex = lines.findIndex(
        (row) => row.itemId === pick.item.id && !row.serialNumber && !serial,
      );
      if (existingIndex >= 0) {
        const existing = lines[existingIndex];
        lines[existingIndex] = {
          ...existing,
          quantity: existing.quantity + 1,
          available,
        };
        continue;
      }

      lines.push({
        itemId: pick.item.id,
        code: pick.item.code,
        name: pick.item.name,
        category: pick.item.category,
        available,
        quantity: 1,
        reserved: false,
        price: pick.item.price || 0,
        serialNumber: serial || undefined,
        equipmentAssetId: pick.equipmentAssetId || undefined,
        unavailableReason: pick.item.unavailableReason,
        icon: pick.item.icon,
      });
    }

    this.lineItems.set(lines);
    const count = picks.length;
    this.closeItemModal();
    this.showToastMessage(
      count === 1
        ? `${picks[0].item.name} added from ${this.selectedLocationName()}`
        : `${count} items added from ${this.selectedLocationName()}`,
      'success',
    );
  }

  removeLineItem(itemId: string, serialNumber = ''): void {
    const item = this.lineItems().find(
      (row) => row.itemId === itemId && (row.serialNumber ?? '') === serialNumber,
    );
    this.lineItems.update((list) =>
      list.filter(
        (row) =>
          !(row.itemId === itemId && (row.serialNumber ?? '') === serialNumber),
      ),
    );
    if (item) {
      this.showToastMessage(`${item.name} removed`, 'info');
    }
  }

  updateLineQuantity(itemId: string, raw: unknown, serialNumber = ''): void {
    const parsed = parsePositiveInt(raw, 'Quantity');
    if (!parsed.ok) {
      this.showToastMessage(
        parsed.message ?? 'Enter a valid quantity',
        'error',
      );
      return;
    }
    if (serialNumber) {
      return;
    }
    this.applyLineQuantity(itemId, parsed.value, serialNumber);
  }

  private applyLineQuantity(
    itemId: string,
    quantity: number,
    serialNumber = '',
  ): boolean {
    const line = this.lineItems().find(
      (row) => row.itemId === itemId && (row.serialNumber ?? '') === serialNumber,
    );
    if (!line) {
      return false;
    }
    const others = this.lineItems()
      .filter(
        (row) =>
          row.itemId === itemId && (row.serialNumber ?? '') !== serialNumber,
      )
      .reduce((sum, row) => sum + row.quantity, 0);
    const available = this.liveAvailableFor(itemId);
    const check = assertCanTake(available - others, quantity, line.name);
    if (!check.ok) {
      this.showToastMessage(
        check.message ?? 'Quantity exceeds remaining available',
        'error',
      );
      return false;
    }
    this.lineItems.update((list) =>
      list.map((row) =>
        row.itemId === itemId && (row.serialNumber ?? '') === serialNumber
          ? { ...row, quantity, available }
          : row,
      ),
    );
    return true;
  }

  updateLinePrice(itemId: string, raw: unknown, serialNumber = ''): void {
    const parsed = parseNonNegativeNumber(raw, 'Price');
    if (!parsed.ok) {
      this.showToastMessage(parsed.message ?? 'Enter a valid price', 'error');
      return;
    }
    this.lineItems.update((list) =>
      list.map((row) =>
        row.itemId === itemId && (row.serialNumber ?? '') === serialNumber
          ? { ...row, price: parsed.value }
          : row,
      ),
    );
  }

  private liveAvailableFor(itemId: string): number {
    const locationName = this.selectedLocationName();
    return Math.max(
      0,
      this.inventory.availableQuantityAtLocation(
        itemId,
        this.branchId(),
        this.locationId(),
        locationName,
      ) -
        this.equipment.unavailableSerialCount(
          Number(itemId) || 0,
          this.branchId(),
          this.locationId(),
          locationName,
        ),
    );
  }

  private syncLineAvailability(): void {
    this.lineItems.update((list) =>
      list.map((row) => ({
        ...row,
        available: this.liveAvailableFor(row.itemId),
      })),
    );
  }

  private firstTakeViolation(): { ok: boolean; message?: string } {
    const totals = new Map<string, { name: string; quantity: number }>();
    for (const line of this.lineItems()) {
      const parsed = parsePositiveInt(line.quantity, line.name);
      if (!parsed.ok) {
        return { ok: false, message: parsed.message };
      }
      const current = totals.get(line.itemId) ?? { name: line.name, quantity: 0 };
      current.quantity += parsed.value;
      totals.set(line.itemId, current);
    }
    for (const [itemId, total] of totals) {
      const check = assertCanTake(
        this.liveAvailableFor(itemId),
        total.quantity,
        total.name,
      );
      if (!check.ok) {
        return check;
      }
    }
    return { ok: true };
  }

  toggleReserved(itemId: string, reserved: boolean, serialNumber = ''): void {
    this.lineItems.update((list) =>
      list.map((row) =>
        row.itemId === itemId && (row.serialNumber ?? '') === serialNumber
          ? { ...row, reserved }
          : row,
      ),
    );
  }

  openConfirmModal(): void {
    if (this.submitting()) {
      return;
    }
    if (!this.isFormValid()) {
      if (!this.issuedToCase().trim()) {
        this.showToastMessage('Enter the recipient name', 'error');
      } else if (!this.recipientPhone().trim()) {
        this.showToastMessage('Enter the recipient cell number', 'error');
      } else if (!this.recipientLocation().trim()) {
        this.showToastMessage('Enter the recipient location', 'error');
      } else if (!this.branchId().trim() || !this.locationId().trim()) {
        this.showToastMessage(
          'Select the branch and store location to take stock from',
          'error',
        );
      } else if (this.lineItems().length === 0) {
        this.showToastMessage('Please add at least one item', 'error');
      } else {
        this.showToastMessage('Please complete the required fields', 'error');
      }
      return;
    }

    this.inventory.loadItemMasterAndStock().subscribe(() => {
      this.syncLineAvailability();
      const violation = this.firstTakeViolation();
      if (!violation.ok) {
        this.showToastMessage(
          violation.message ??
            `An item exceeds remaining available at ${this.selectedLocationName()}`,
          'error',
        );
        return;
      }

      this.showConfirmModal.set(true);
      document.body.style.overflow = 'hidden';
    });
  }

  closeConfirmModal(): void {
    this.showConfirmModal.set(false);
    document.body.style.overflow = 'auto';
  }

  confirmIssue(): void {
    if (this.submitting()) {
      return;
    }
    this.submitIssue('issued');
  }

  saveDraft(): void {
    this.showToastMessage(
      'Save as pending is disabled. Issue or reserve from Current Stock.',
      'info',
    );
  }

  private submitIssue(status: 'issued' | 'pending'): void {
    const issuedTo = this.issuedToCase().trim();
    const phone = this.recipientPhone().trim();
    const dest = this.recipientLocation().trim();
    const branchId = this.branchId();
    const locationId = this.locationId();
    if (
      !issuedTo ||
      !phone ||
      !dest ||
      this.lineItems().length === 0 ||
      !branchId ||
      !locationId
    ) {
      this.openConfirmModal();
      return;
    }

    if (this.submitting()) {
      return;
    }
    this.submitting.set(true);

    const branchName = this.selectedBranchName() || branchId;
    const locationName = this.selectedLocationName();

    this.inventory
      .loadItemMasterAndStock()
      .pipe(
        switchMap(() => {
          this.syncLineAvailability();
          const violation = this.firstTakeViolation();
          if (!violation.ok) {
            return of({
              success: false as const,
              message: violation.message,
              hadReserved: false,
              hadConsumable: false,
            });
          }

          const lines = this.lineItems().map((line) => ({
            inventoryItemID: Number(line.itemId) || 0,
            quantity: line.quantity,
            unitCost: Number(line.price) || 0,
            itemName: line.serialNumber
              ? `${line.name} (${line.serialNumber})`
              : line.name,
            isReserved: line.reserved,
            serialNumber: line.serialNumber,
            equipmentAssetId: Number(line.equipmentAssetId) || undefined,
          }));

          return this.inventory
            .createIssueStockViaApi({
              branchId,
              branchName,
              storeLocationId: locationId,
              locationName,
              issuedTo,
              recipientPhone: phone,
              recipientLocation: dest,
              issueDate: this.issueDate(),
              notes: this.notes(),
              status,
              lines,
            })
            .pipe(
              map((issueResult) => ({
                ...issueResult,
                hadReserved: lines.some((line) => line.isReserved),
                hadConsumable: lines.some((line) => !line.isReserved),
              })),
            );
        }),
        finalize(() => this.submitting.set(false)),
      )
      .subscribe((result) => {
        if (result.success) {
          this.finishIssueSuccess(issuedTo, {
            hadReserved: result.hadReserved,
            hadConsumable: result.hadConsumable,
          });
          this.inventory.refreshFromApi();
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to issue stock',
            'error',
          );
        }
      });
  }

  private finishIssueSuccess(
    issuedTo: string,
    split: { hadReserved: boolean; hadConsumable: boolean },
  ): void {
    this.lastIssuedTo.set(issuedTo);
    this.closeConfirmModal();
    this.lineItems.set([]);
    this.issuedToCase.set('');
    this.recipientPhone.set('');
    this.recipientLocation.set('');
    this.notes.set('');
    const parts: string[] = [];
    if (split.hadConsumable) {
      parts.push('issued items are on Issued Stock');
    }
    if (split.hadReserved) {
      parts.push(
        'reserved lines are on Issued Stock as Reserved — use Issue now to complete',
      );
    }
    const detail = parts.length ? ` ${parts.join('. ')}.` : '';
    this.showToastMessage(
      `Stock submitted for ${issuedTo}.${detail}`,
      'success',
    );
  }

  openEditIssueModal(issue: StockIssue): void {
    this.editingIssueId.set(issue.id);
    const branchId =
      this.branches().find(
        (row) => row.id === issue.branch || row.name === issue.branch,
      )?.id ?? '';

    this.editingIssueStatus.set(issue.status);
    this.editIssue.set({
      issuedTo: issue.issuedTo,
      branch: branchId,
      issueDate: issue.issueDate,
      items: issue.items,
      status: issue.status,
    });
    this.issuedBy.set(this.auth.currentUserName());
    this.showEditIssueModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeEditIssueModal(): void {
    this.showEditIssueModal.set(false);
    this.editingIssueId.set(null);
    this.editingIssueStatus.set(null);
    document.body.style.overflow = 'auto';
  }

  updateEditIssue(
    field: keyof ReturnType<typeof this.editIssue>,
    value: string | number,
  ): void {
    if (field === 'status' && this.editingIssueStatus() === 'issued') {
      return;
    }
    if (field === 'items') {
      const parsed = parsePositiveInt(value, 'Items count');
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Enter a valid item count',
          'error',
        );
        return;
      }
      value = parsed.value;
    }
    this.editIssue.update((row) => ({ ...row, [field]: value }));
  }

  submitEditIssue(): void {
    if (this.saving()) {
      return;
    }

    const issue = this.editIssue();
    const editingId = this.editingIssueId();
    if (!editingId || !issue.issuedTo || !issue.branch || !issue.issueDate) {
      this.showToastMessage('Please fill in all required fields', 'error');
      return;
    }

    if (this.editingIssueStatus() === 'issued' && issue.status !== 'issued') {
      this.showToastMessage(
        'This stock has been issued. The status cannot be changed.',
        'error',
      );
      this.editIssue.update((row) => ({ ...row, status: 'issued' }));
      return;
    }

    const status =
      this.editingIssueStatus() === 'issued' ? 'issued' : issue.status;
    const branchName =
      this.branches().find((row) => row.id === issue.branch)?.name ??
      issue.branch;

    this.saving.set(true);
    this.inventory
      .updateIssueStockViaApi(editingId, {
        issuedTo: issue.issuedTo,
        issueDate: issue.issueDate,
        status,
      })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe((result) => {
        if (result.success) {
          this.inventory.updateStockIssue(editingId, {
            issuedTo: issue.issuedTo,
            branch: branchName,
            issueDate: issue.issueDate,
            items: issue.items,
            status,
          });
          this.closeEditIssueModal();
          this.showToastMessage('Issue updated successfully', 'success');
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to update issue',
            'error',
          );
        }
      });
  }

  resetForm(): void {
    if (this.lineItems().length > 0 || this.issuedToCase()) {
      if (!confirm('Discard all changes?')) {
        return;
      }
    }
    this.lineItems.set([]);
    this.issuedToCase.set('');
    this.recipientPhone.set('');
    this.recipientLocation.set('');
    this.issueDate.set(new Date().toISOString().slice(0, 10));
    this.issuedBy.set(this.auth.currentUserName());
    this.locationId.set('');
    this.notes.set('');
    this.showToastMessage('Form reset', 'info');
  }

  private showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  exportData(): void {
    const rows = this.issueStockList().map((issue) => ({
      'Issue No.': issue.issueNumber,
      'Issued To': issue.issuedTo,
      Phone: issue.recipientPhone ?? '',
      Destination: issue.recipientLocation ?? '',
      Item: issue.itemName ?? '',
      Branch: issue.branch,
      Location: issue.location ?? '',
      Qty: issue.items,
      'Total Value': issue.totalValue ?? 0,
      Date: issue.issueDate,
      Status: this.issueStatusLabel(issue.status),
    }));

    if (!rows.length) {
      this.showToastMessage('No issues or reserves to export yet.', 'info');
      return;
    }

    this.exporter.downloadCsv(
      `issued-stock-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }

  issueStatusLabel(status: StockIssue['status']): string {
    const map: Record<StockIssue['status'], string> = {
      pending: 'Pending',
      reserved: 'Reserved',
      issued: 'Issued',
      cancelled: 'Cancelled',
    };
    return map[status];
  }

  issueReservedHold(issue: StockIssue): void {
    if (
      this.submitting() ||
      (issue.status !== 'reserved' && !issue.isReservedHold)
    ) {
      return;
    }
    this.submitting.set(true);
    this.inventory
      .updateIssueStockViaApi(issue.id, {
        issuedTo: issue.issuedTo,
        issueDate: issue.issueDate,
        status: 'issued',
      })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            `Reserved stock ${issue.issueNumber} issued to ${issue.issuedTo}.`,
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to issue reserved stock',
            'error',
          );
        }
      });
  }

  releaseReservedHold(issue: StockIssue): void {
    if (
      this.submitting() ||
      (issue.status !== 'reserved' && !issue.isReservedHold)
    ) {
      return;
    }
    if (
      !confirm(
        `Release reserved hold ${issue.issueNumber}? Stock becomes available again.`,
      )
    ) {
      return;
    }
    this.submitting.set(true);
    this.inventory
      .updateIssueStockViaApi(issue.id, {
        issuedTo: issue.issuedTo,
        issueDate: issue.issueDate,
        status: 'cancelled',
      })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            `Reserved hold ${issue.issueNumber} released.`,
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to release reserved hold',
            'error',
          );
        }
      });
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if (
      (event.target as HTMLElement).classList.contains('modal-backdrop') ||
      (event.target as HTMLElement).classList.contains('modal-overlay') ||
      (event.target as HTMLElement).classList.contains('modal')
    ) {
      this.closeItemModal();
      this.closeConfirmModal();
      this.closeEditIssueModal();
    }
  }
}
